/**
 * Role change confirmation dialog.
 * Shows current role, role selector dropdown, and confirmation.
 * OWNER role requires special handling (ownership transfer warning).
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { TeamMember, TeamRole } from '../../types/company';
import RoleBadge from './RoleBadge';

interface RoleChangeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  member: TeamMember;
  onConfirm: (newRole: TeamRole) => Promise<void>;
  currentUserRole?: TeamRole;
}

// Available roles for role changes (OWNER can only be selected for ownership transfer)
const AVAILABLE_ROLES: TeamRole[] = ['ADMIN', 'AGENT', 'VIEWER'];

// Role options including OWNER (for ownership transfer scenario)
const ALL_ROLES: TeamRole[] = ['OWNER', 'ADMIN', 'AGENT', 'VIEWER'];

export default function RoleChangeDialog({
  isOpen,
  onClose,
  member,
  onConfirm,
  currentUserRole,
}: RoleChangeDialogProps) {
  const { t } = useTranslation('team');
  const [selectedRole, setSelectedRole] = useState<TeamRole>(member.role);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when dialog opens or member changes
  useEffect(() => {
    if (isOpen) {
      setSelectedRole(member.role);
      setIsLoading(false);
      setError(null);
    }
  }, [isOpen, member.role]);

  // Lock body scroll and handle ESC key
  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isLoading) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, isLoading]);

  if (!isOpen) return null;

  const isOwnerBeingEdited = member.role === 'OWNER';
  const canSelectOwner = currentUserRole === 'OWNER' && isOwnerBeingEdited;
  const availableRoles = canSelectOwner ? ALL_ROLES : AVAILABLE_ROLES;
  const hasChanges = selectedRole !== member.role;

  const handleConfirm = async () => {
    if (!hasChanges) return;

    setIsLoading(true);
    setError(null);

    try {
      await onConfirm(selectedRole);
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to change role';
      setError(message);
      setIsLoading(false);
    }
  };

  const getRoleLabel = (role: TeamRole): string => {
    switch (role) {
      case 'OWNER':
        return t('roleOwner');
      case 'ADMIN':
        return t('roleAdmin');
      case 'AGENT':
        return t('roleAgent');
      case 'VIEWER':
        return t('roleViewer');
      default:
        return role;
    }
  };

  const dialogContent = (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      {/* Backdrop click to close */}
      <div
        className="absolute inset-0 cursor-pointer"
        aria-hidden="true"
        onClick={isLoading ? undefined : onClose}
      />

      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="role-change-dialog-title"
        className="relative z-[1] w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b p-6">
          <h2
            id="role-change-dialog-title"
            className="text-lg font-semibold text-[var(--pv-color-primary)]"
          >
            {t('changeRoleTitle')}
          </h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 transition-colors hover:text-gray-600 cursor-pointer disabled:opacity-50"
            aria-label="Close dialog"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Member info */}
          <div className="flex items-center gap-3">
            {member.avatarUrl ? (
              <img
                src={member.avatarUrl}
                alt={member.displayName}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                <span className="text-gray-600 font-medium text-sm">
                  {member.displayName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <p className="font-medium text-gray-900">{member.displayName}</p>
              <p className="text-sm text-gray-500">{member.email}</p>
            </div>
          </div>

          {/* Current role display */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>{t('currentRole', 'Current role:')}</span>
            <RoleBadge role={member.role} size="sm" />
          </div>

          {/* OWNER warning */}
          {isOwnerBeingEdited && !canSelectOwner && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <i className="ri-alert-line text-amber-600 mt-0.5" />
                <p className="text-sm text-amber-800">
                  {t('ownerProtection')}
                </p>
              </div>
            </div>
          )}

          {/* Role selector */}
          {(!isOwnerBeingEdited || canSelectOwner) && (
            <div>
              <label
                htmlFor="role-select"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {t('newRole', 'New role')}
              </label>
              <select
                id="role-select"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value as TeamRole)}
                disabled={isLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-100"
              >
                {availableRoles.map((role) => (
                  <option key={role} value={role}>
                    {getRoleLabel(role)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Confirmation message */}
          {hasChanges && (
            <p className="text-sm text-gray-600">
              {t('changeRoleConfirm', {
                name: member.displayName,
                role: getRoleLabel(selectedRole),
              })}
            </p>
          )}

          {/* Error display */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 cursor-pointer"
          >
            {t('cancel')}
          </button>
          {(!isOwnerBeingEdited || canSelectOwner) && (
            <button
              onClick={handleConfirm}
              disabled={isLoading || !hasChanges}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
            >
              {isLoading ? (
                <>
                  <i className="ri-loader-4-line mr-2 animate-spin inline-block" />
                  Loading...
                </>
              ) : (
                'Confirm'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined') {
    return createPortal(dialogContent, document.body);
  }

  return dialogContent;
}
